import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { InlineCTA } from "@/components/InlineCTA";
import LocationSection from "@/components/LocationSection";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { REVIEW_COUNT } from "@/config/reviews";
import { MapPin } from "lucide-react";
import heroImage from "@/assets/paver-patio-pennington-park-paterson-nj-hero.webp";
import vanImage from "@/assets/masonry-van-pennington-park-paterson-neighborhood-street.webp";
import detailImage from "@/assets/herringbone-paver-detail-pennington-park-paterson-duplex.webp";
import backyardImage from "@/assets/paver-patio-installation-pennington-park-paterson-backyard.webp";
import truckImage from "@/assets/masonry-truck-street-pennington-park-paterson-paver-samples.webp";

const faqs = [
  {
    question: "How far is Masonry Pro Construction from Pennington Park?",
    answer:
      "We are a short drive. Our shop is at 36 Highview Ave in Totowa, which sits across the Passaic River from the Stoney Road section where Pennington Park is. That closeness is part of why we take a lot of work in this corner of Paterson.",
  },
  {
    question: "How deep does the base need to be for a paver patio in Paterson?",
    answer:
      "Base depth usually runs around 6 to 10 inches, depending on your soil and what the patio needs to carry. We confirm the number after looking at the yard. Getting this right is what keeps a patio level through freeze and thaw winters.",
  },
  {
    question: "Can you build a paver patio in a small Paterson backyard?",
    answer:
      "Yes. Plenty of yards near Pennington Park are narrow, and small patios are some of the most used spaces we build. The planning shifts toward layout and access rather than square footage, and we work out the material drop before the truck shows up.",
  },
];

export default function PaverPatioPenningtonPark() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Paver Patio Near Pennington Park Paterson NJ | Masonry Pro</title>
        <meta
          name="description"
          content="Paver patio installation near Pennington Park in Paterson, NJ. Masonry Pro Construction builds on a proper base so your patio stays level. Free estimates."
        />
        <meta name="author" content="Masonry Pro Construction" />
        <meta
          name="keywords"
          content="masonry contractor, paver patio, retaining wall, stone work, North Jersey, Bergen County, Essex County, Passaic County"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Paver Patio Near Pennington Park Paterson NJ | Masonry Pro" />
        <meta
          property="og:description"
          content="Paver patio installation near Pennington Park in Paterson, NJ. Masonry Pro Construction builds on a proper base so your patio stays level. Free estimates."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@MasonryPro" />
        <meta name="twitter:image" content="https://masonry-pro.com/masonry-pro-logo-2.webp" />
        <meta name="twitter:title" content="Paver Patio Near Pennington Park Paterson NJ | Masonry Pro" />
        <meta
          name="twitter:description"
          content="Paver patio installation near Pennington Park in Paterson, NJ. Masonry Pro Construction builds on a proper base so your patio stays level. Free estimates."
        />
        <link rel="preload" as="image" href={heroImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            generateServiceSchema({
              name: "Paver Patio Installation Near Pennington Park, Paterson NJ",
              description:
                "Paver patio installation near Pennington Park in Paterson, NJ. Masonry Pro Construction builds on a proper base so your patio stays level.",
              url: "/service-areas/passaic-county/paver-patio-pennington-park-paterson",
              image: "https://masonry-pro.com/og/paver-patios.jpg",
            })
          )}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative">
            <img
              src={heroImage}
              alt="Veterans memorial and tank display at Pennington Park in Paterson, NJ"
              width={1200}
              height={675}
              fetchPriority="high"
              decoding="async"
              className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 to-black/65 flex items-center justify-center">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                    <span style={{ color: '#d4a853', fontWeight: 500 }}>Paterson, Passaic County, NJ</span>
                  </div>
                  <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                    Paver Patio Installation Near{" "}
                    <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Pennington Park in Paterson, NJ</span>
                  </h1>
                  <p style={{ color: '#c4a24e', WebkitTextFillColor: '#c4a24e', fontSize: '0.95rem', fontWeight: 500, letterSpacing: '0.025em', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                    <Link to="/paving-services/paver-patios" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Paver Patios</Link> | <Link to="/service-areas/passaic-county" style={{ color: '#c4a24e', textDecoration: 'none' }} className="hover:underline">Passaic County</Link> | Free Estimates, No Trip Fees
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  Masonry Pro Construction is a family-owned masonry contractor based in Totowa. We install paver patios, walkways, and retaining walls for homeowners across North Jersey. Pennington Park sits just across the Passaic River from our shop, so this part of Paterson is close to home for us.
                </p>
              </div>
            </div>
          </section>

          <LocationSection
            heading="Paver Patios in the"
            headingHighlight="Stoney Road Section of Paterson"
            imageSrc={vanImage}
            imageAlt="Masonry Pro van on a residential street beside Pennington Park in Paterson, NJ, with paver pallets staged on a driveway"
            imageSide="right"
            altBackground={false}
          >
            <p>
              Pennington Park sits on McBride Avenue, down in the Stoney Road section at the southwest corner of the city. Our shop in Totowa is on the other side of the Passaic River. It is a short drive. Most mornings our crew is on your street before the traffic on Route 80 builds up.
            </p>
            <p>
              That proximity can matter a lot. If a pallet shows up short, or someone on the crew needs a different blade, or the homeowner has a question about where a step should be placed. We're only ten minutes away. We can handle those things the same day instead of having to wait until the next day.
            </p>
            <p>
              The yards of the homes around Pennington Park, in Paterson, are usually a little narrower. They are older homes with deep lots, a driveway on one side, and a neighbor's fence on the other. We look at all of that before anyone breaks ground. We work across{" "}
              <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic County</Link>
              , and this corner of Paterson is one of the areas we are in most often.
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Access for the truck and where we can stage pallets without blocking the street</li>
              <li>Where water goes now, and where it should go after</li>
              <li>How the patio ties into an existing walk, stoop, or driveway</li>
              <li>Whether the yard needs a step or a small wall to deal with the grade</li>
            </ul>
            <p>
              Trucks are the part homeowners rarely think about. Some of these streets are tight, with cars parked on both sides. We would rather figure out where to unload the materials and how the wheelbarrow will route on the estimate visit than discover it the morning the material arrives.
            </p>
          </LocationSection>

          <LocationSection
            heading="What the Ground Around Pennington Park"
            headingHighlight="Does to a Patio"
            imageSrc={detailImage}
            imageAlt="Setting a paver against a step riser on bedding sand, Paterson NJ"
            imageSide="left"
            altBackground={true}
          >
            <p>A paver patio is not one thing. It is layers, and most of them are buried.</p>
            <p>
              We dig down first. Base depth usually runs somewhere around 6 to 10 inches, though it depends on your soil and what the patio has to hold. Then we add compacted gravel, then a bedding layer of sand, then the pavers themselves. Polymeric sand gets swept into the joints last and compacted one more time.
            </p>
            <p>Skip a step and the patio still looks fine in June. You find out in March.</p>
            <p>
              Here is why that matters in Paterson specifically. Water freezes, expands, and pushes. Do that a few dozen times over a winter, and a patio built on a thin base starts to move. Corners lift. Joints open. A poured concrete slab cracks straight across, and you are looking at the whole thing. With pavers, we can pull the affected section, fix what is underneath, and set the same units back down.
            </p>
            <p>
              Ground closer to the river and the low areas near the park can hold water longer after a heavy rain. We check for that during the estimate. If the yard drains slowly, the fix is usually pitch and base depth, not anything extreme.
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Slope is planned away from the house, not toward it</li>
              <li>Edge restraint runs the full perimeter so the field cannot spread</li>
              <li>Joints get polymeric sand, which also keeps weeds and ants out</li>
              <li>Damaged sections can be lifted and reset without redoing the patio</li>
            </ul>
          </LocationSection>

          <LocationSection
            heading="Choosing Pavers for a"
            headingHighlight="Paterson Backyard"
            imageSrc={backyardImage}
            imageAlt="Mason leveling a paver patio in a Paterson backyard near Pennington Park"
            imageSide="right"
            altBackground={false}
          >
            <p>
              Most homeowners around here end up choosing concrete pavers. They hold up to freeze and thaw, they take foot traffic, and replacement units are easy to source years later. Clay brick and natural stone are both on the table too. We will show you what we carry and give you a straight answer about what fits the house.
            </p>
            <p>
              Every yard sits a little differently, so the material conversation happens after we have walked the space with you.{" "}
              <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">Learn more about our paver patio installation process</Link>. If your project is closer to the center of the city, we cover that too on our <Link to="/service-areas/passaic-county/paver-patio-downtown-paterson" className="text-secondary hover:underline">downtown Paterson paver patio</Link> page.
            </p>
          </LocationSection>

          <InlineCTA
            heading="Thinking About a Patio Near Pennington Park?"
            highlightedHeading="We're Ten Minutes Away"
            description="Call or text us directly or scroll down to request your free estimate."
          />

          <LocationSection
            heading="Talk to Us About Your"
            headingHighlight="Pennington Park Paver Patio"
            imageSrc={truckImage}
            imageAlt="Reviewing paver samples at the truck on a Paterson street"
            imageSide="left"
            altBackground={true}
          >
            <p>
              Homeowners near Murray Avenue, over by the reservoir, and up along the blocks toward Hayden Heights call us with the same handful of questions. How much room do I actually have? Will it drain? What will this cost?
            </p>
            <p>Those are better answered standing in the yard than over the phone.</p>
            <p>
              Masonry Pro Construction is at 36 Highview Ave in Totowa, New Jersey. You can reach us at (973) 725-2395. Estimates are free, and there is no trip fee, so a visit costs you nothing but the time it takes to walk the yard with us.
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Address: 36 Highview Ave, Totowa, NJ 07512</li>
              <li>Phone: (973) 725-2395 for scheduling, free estimates, or questions</li>
              <li>Service area: Passaic, Bergen, Essex, and Morris counties</li>
              <li>Biagio has been laying masonry for over 35 years and is a bricklayers union member</li>
            </ul>
            <p>
              We have been doing this as a family for a long time, and we have picked up more than {REVIEW_COUNT}+ five-star Google reviews along the way. Gino has been on job sites with his father for over 14 years. When you call, you are talking to the people who will be standing in your yard.
            </p>
            <p>
              Reach out early. If you want the patio done by June, a call in early spring gives us time to look at the yard, sort out drainage, and order materials without rushing. We do book up fast, so we want to make sure you secure your spot sooner rather than later.
            </p>
          </LocationSection>

          {/* FAQ Cards */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions About Paver Patios Near{" "}
                <span className="text-gradient-gold">Pennington Park</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <CTASection
            serviceName="Pennington Park Paver Patio"
            heading="Get a Free Estimate on Your"
            highlightedHeading="Pennington Park Paver Patio"
          />
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-2">
                <p className="text-xl font-bold">Masonry Pro Construction</p>
                <p className="text-muted-foreground">36 Highview Ave, Totowa, NJ 07512</p>
                <a href="tel:9737252395" className="text-secondary hover:underline text-lg font-semibold block">
                  (973) 725-2395
                </a>
                <p className="text-sm text-muted-foreground italic mt-4">
                  NJ HIC License{" "}
                  <a href="https://newjersey.mylicense.com/verification/Search.aspx?facility=Y" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors underline">#13VH06777200</a>
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
